import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import type { Restaurant } from '@/api/types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { pricesApi } from '@/api/endpoints/prices'

interface BulkPriceModalProps {
  open: boolean
  onClose: () => void
  selectedSkus: Array<string>
  restaurants: Array<Restaurant>
}

export function BulkPriceModal({
  open,
  onClose,
  selectedSkus,
  restaurants,
}: BulkPriceModalProps) {
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const [restaurantId, setRestaurantId] = useState<string>('')
  const [priceEur, setPriceEur] = useState('')
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async () => {
      const amountCents = Math.round(parseFloat(priceEur) * 100)
      const updates = selectedSkus.map((sku) => ({
        sku,
        restaurantId: parseInt(restaurantId),
        amountCents,
      }))

      return await pricesApi.bulkUpdate(updates)
    },
    onSuccess: ({ succeeded, failed }) => {
      // Invalidate all price queries
      selectedSkus.forEach((sku) => {
        queryClient.invalidateQueries({ queryKey: ['prices', sku] })
      })

      if (failed === 0) {
        toast.success(tProducts('bulkPrice.success', { count: succeeded }))
      } else {
        toast.warning(tProducts('bulkPrice.partial', { succeeded, failed }))
      }
      handleClose()
    },
    onError: () => {
      toast.error(tProducts('bulkPrice.error'))
    },
  })

  const handleClose = () => {
    setRestaurantId('')
    setPriceEur('')
    onClose()
  }

  const handleSubmit = () => {
    if (!restaurantId) {
      toast.error(tProducts('bulkPrice.selectRestaurant'))
      return
    }
    const price = parseFloat(priceEur)
    if (isNaN(price) || price < 0) {
      toast.error(tProducts('bulkPrice.invalidPrice'))
      return
    }
    mutation.mutate()
  }

  const handlePriceChange = (value: string) => {
    // Allow only numbers and one decimal point
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setPriceEur(value)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tProducts('bulkPrice.title')}</DialogTitle>
          <DialogDescription>
            {tProducts('bulkPrice.description', { count: selectedSkus.length })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="restaurant">
              {tProducts('bulkPrice.restaurantLabel')}
            </Label>
            <Select value={restaurantId} onValueChange={setRestaurantId}>
              <SelectTrigger id="restaurant">
                <SelectValue
                  placeholder={tProducts('bulkPrice.restaurantPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                {restaurants.map((restaurant) => (
                  <SelectItem
                    key={restaurant.id}
                    value={restaurant.id.toString()}
                  >
                    {restaurant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="price">{tProducts('bulkPrice.priceLabel')}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                €
              </span>
              <Input
                id="price"
                type="text"
                inputMode="decimal"
                placeholder={tProducts('bulkPrice.pricePlaceholder')}
                value={priceEur}
                onChange={(e) => handlePriceChange(e.target.value)}
                className="pl-7"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {tProducts('bulkPrice.priceHelp')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            {tCommon('cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending}>
            {mutation.isPending && <Spinner className="w-4 h-4 mr-2" />}
            {tProducts('bulkPrice.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
